import type {GetSubscriptionApiResponse} from "../../../react-query-hooks/store/subscriptions/get-subscription/types";
import type {OfferingFormCheckoutFormValues} from "../../../components/offering-form/general/checkout-form/types";
import type {SubscriptionTierEntity} from "../../../react-query-hooks/store/subscriptions/tiers/types";
import {inferBillingFrequency} from "../../../react-query-hooks/store/subscriptions/constants/billing-enums";
import type {EditSubscriptionBasicFormValues, EditSubscriptionTierFormRow} from "./types";

function isTierCurrencyAutoConverted(currency: unknown): boolean {
  if (!currency || typeof currency !== "object") {
    return false;
  }
  const row = currency as {isAutoConverted?: unknown; is_auto_converted?: unknown};
  return row.isAutoConverted === true || row.is_auto_converted === true;
}

/** A saved tier as the edit form holds it. */
export function mapTierEntityToFormRow(
  tier: SubscriptionTierEntity
): EditSubscriptionTierFormRow {
  const billingCount =
    tier.billingCycles != null && tier.billingCycles > 0 ? "custom" : "until_cancel";
  const frequency = inferBillingFrequency(tier.billingInterval, tier.billingDuration);
  const manualCurrencies =
    tier.currencies?.filter((currency) => !isTierCurrencyAutoConverted(currency)) ?? [];
  const hasTelegramGroup = Boolean(tier.telegramGroupChatId?.trim());
  const hasRedirectUrl = Boolean(tier.urlRedirect?.trim());
  const redirectType =
    tier.redirectType === "telegram" && hasTelegramGroup
      ? "telegram"
      : tier.redirectType === "url" && hasRedirectUrl
      ? "url"
      : hasTelegramGroup
      ? "telegram"
      : hasRedirectUrl
      ? "url"
      : tier.redirectType ?? "none";
  return {
    apiTierId: tier.id,
    tierName: tier.name,
    // A Pay What You Want tier is never Free, whatever its Suggested Price reads as.
    priceType: tier.isPayWhatYouWant ? "pwyw" : tier.defaultPrice > 0 ? "paid" : "free",
    currencies: manualCurrencies.length
      ? manualCurrencies
      : [
          {
            callPriceCurrency: tier.defaultCurrency,
            callPrice: tier.defaultPrice,
            ...(tier.defaultMinPrice === undefined
              ? {}
              : {minCallPrice: tier.defaultMinPrice}),
          },
        ],
    billingFrequency: frequency as EditSubscriptionTierFormRow["billingFrequency"],
    billingCount,
    billingCycles: tier.billingCycles ?? undefined,
    featuresEnabled: tier.features.length > 0,
    features: tier.features.map((value, i) => ({
      id: `f-${tier.id}-${i}`,
      value,
    })),
    redirectAfterPurchaseEnabled: redirectType !== "none",
    redirectAfterPurchase: redirectType === "url" ? "custom-url" : redirectType,
    telegramGroupId: tier.telegramGroupChatId ?? undefined,
    urlRedirect: tier.urlRedirect ?? null,
  };
}

export function mapSubscriptionToBasicFormValues(
  data: GetSubscriptionApiResponse
): EditSubscriptionBasicFormValues {
  const sub = data.productSubscription;
  const redirect = sub.redirectUrl;
  return {
    name: sub.name,
    description: sub.description ?? "",
    hideFromProfile: sub.hideFromProfile ?? false,
    requestCustomerPhoneNumber: sub.requestPhoneNumber ?? false,
    isUrlRedirect: !!redirect && redirect.length > 0,
    url_redirect: redirect ?? "",
    limitUnitsPerTransaction: false,
    enable_currency_conversion: true,
    selected_currencies: sub.selectedCurrencies?.length
      ? [...sub.selectedCurrencies]
      : [],
    tiers: sub.tiers.map(mapTierEntityToFormRow),
  };
}

export function mapApiBookingQuestionsToCheckoutForm(
  questions: string[] | undefined
): OfferingFormCheckoutFormValues["bookingQuestions"] {
  return (questions ?? []).map((question) => ({
    question,
    answerType: "short-text" as const,
    required: false,
    options: [{optionLabel: ""}],
  }));
}
